import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Calendar, MapPin, Clock, Volume2, VolumeX, Heart } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
} from '@/src/infrastructure/lib/weddingDataHelper';
import { safeImageUrl, safeLinkUrl } from '@/src/modules/renderer/url-safety';
import './theme.css';

const META: TemplateMetadata = {
  id: 'exp-vedic-mandir-divine',
  name: 'Vedic Mandir Divine',
  occasionType: 'WEDDING',
  description:
    'Sacred temple-inspired wedding microsite: saffron, maroon and gold, multi-ceremony schedule, diya ambience, RSVP and maps.',
  isPremium: true,
  tags: ['wedding', 'temple', 'vedic', 'sacred', 'saffron', 'multi-ceremony', 'premium', 'hindu'],
  colorScheme: { primary: '#8B1E1E', accent: '#D4AF37', bg: '#1A0A0A' },
};

type Ceremony = { key: string; label: string; date?: string; time?: string; venue?: string };

function pickStr(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

export function VedicMandirDivine({ data }: { data: InvitationData }) {
  const [muted, setMuted] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const expMeta = (data.customData as { expMeta?: Record<string, unknown> } | null)?.expMeta ?? {};
  const wedding = data.customData?.wedding;
  const extracted = extractCoupleNames(data.eventName);

  const groomName = pickStr(
    wedding?.groom?.fullName,
    wedding?.groom?.nickname,
    expMeta.groomName,
    expMeta.secondaryName,
    extracted?.groom,
  );
  const brideName = pickStr(
    wedding?.bride?.fullName,
    wedding?.bride?.nickname,
    expMeta.brideName,
    expMeta.primaryName,
    extracted?.bride,
  );
  const coupleTitle =
    groomName && brideName ? `${groomName} & ${brideName}` : groomName || brideName || data.eventName || '';

  const parsedDate = parseFlexibleDate(data.eventDate);
  const eventDateLabel = parsedDate
    ? parsedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : pickStr(data.eventDate, expMeta.weddingDate as string, expMeta.eventDate as string);

  const venue = pickStr(data.eventVenue, wedding?.weddingEvents?.[0]?.venue, expMeta.venue as string);
  const hostName = pickStr(data.hostName, expMeta.hostName as string);
  const tagline = pickStr(
    data.customMessage,
    expMeta.tagline as string,
    'With the blessings of elders, we invite you to our sacred union.',
  );

  const bridePhoto = safeImageUrl(
    pickStr(
      (expMeta.referenceImages as { bride?: string } | undefined)?.bride,
      expMeta.bridePhotoUrl as string,
      (data.customData as { bridePhotoUrl?: string } | null)?.bridePhotoUrl,
    ),
  );
  const groomPhoto = safeImageUrl(
    pickStr(
      (expMeta.referenceImages as { groom?: string } | undefined)?.groom,
      expMeta.groomPhotoUrl as string,
      (data.customData as { groomPhotoUrl?: string } | null)?.groomPhotoUrl,
    ),
  );
  const couplePhoto = safeImageUrl(
    pickStr(
      (expMeta.referenceImages as { couple?: string; primary?: string } | undefined)?.couple,
      (expMeta.referenceImages as { primary?: string } | undefined)?.primary,
      expMeta.couplePhotoUrl as string,
      data.mediaUrls?.[0],
    ),
  );
  const customVideoUrl = safeLinkUrl(
    pickStr(
      (data.customData as { customVideoUrl?: string; preWeddingVideoUrl?: string } | null)?.customVideoUrl,
      (data.customData as { preWeddingVideoUrl?: string } | null)?.preWeddingVideoUrl,
      expMeta.customVideoUrl as string,
    ),
  );
  const rsvpHref = safeLinkUrl(data.rsvpLink);

  const ceremonies: Ceremony[] = useMemo(() => {
    const events = wedding?.weddingEvents ?? [];
    const labels = ['Haldi', 'Mehendi', 'Sangeet', 'Vivah', 'Reception'];
    if (events.length > 0) {
      return events.map((ev, i) => ({
        key: `c-${i}`,
        label: pickStr(ev.title, labels[i], `Ceremony ${i + 1}`),
        date: ev.date || eventDateLabel,
        time: ev.startTime || data.eventTime || '',
        venue: ev.venue || venue,
      }));
    }
    return labels.map((label, i) => ({
      key: `d-${i}`,
      label,
      date: eventDateLabel,
      time: i === 3 ? data.eventTime || '' : '',
      venue,
    }));
  }, [wedding?.weddingEvents, eventDateLabel, data.eventTime, venue]);

  const mapsUrl = venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`
    : '';

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) {
      setMuted((m) => !m);
      return;
    }
    if (muted) {
      void el.play().catch(() => undefined);
      setMuted(false);
    } else {
      el.pause();
      setMuted(true);
    }
  };

  return (
    <div className="vmd-root" data-template="exp-vedic-mandir-divine">
      <audio ref={audioRef} loop preload="none" src="https://cdn.pixabay.com/download/audio/2022/03/24/audio_c8c8a73467.mp3?filename=temple-bells-ambient-110544.mp3" />
      <button type="button" className="vmd-audio" onClick={toggleAudio} aria-label={muted ? 'Play ambient audio' : 'Mute audio'}>
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      <section className="vmd-hero">
        <div className="vmd-mandala" aria-hidden />
        <p className="vmd-eyebrow">॥ शुभ विवाह ॥</p>
        <motion.h1
          className="vmd-names"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {coupleTitle || 'Sacred Union'}
        </motion.h1>
        <p className="vmd-event">{data.eventName || 'Wedding Celebration'}</p>
        <div className="vmd-meta-row">
          {eventDateLabel ? (
            <span>
              <Calendar size={14} /> {eventDateLabel}
            </span>
          ) : null}
          {data.eventTime ? (
            <span>
              <Clock size={14} /> {data.eventTime}
            </span>
          ) : null}
          {venue ? (
            <span>
              <MapPin size={14} /> {venue}
            </span>
          ) : null}
        </div>
        {hostName ? <p className="vmd-host">Hosted by {hostName}</p> : null}
        <a className="vmd-cta" href="#vmd-rsvp">
          <Heart size={14} /> RSVP
        </a>
      </section>

      {(bridePhoto || groomPhoto || couplePhoto) && (
        <section className="vmd-photos">
          {groomPhoto ? (
            <figure className="vmd-avatar">
              <img src={groomPhoto} alt={groomName || 'Groom'} />
              <figcaption>{groomName || 'Groom'}</figcaption>
            </figure>
          ) : null}
          {couplePhoto ? (
            <figure className="vmd-avatar vmd-avatar--couple">
              <img src={couplePhoto} alt={coupleTitle || 'Couple'} />
              <figcaption>Together</figcaption>
            </figure>
          ) : null}
          {bridePhoto ? (
            <figure className="vmd-avatar">
              <img src={bridePhoto} alt={brideName || 'Bride'} />
              <figcaption>{brideName || 'Bride'}</figcaption>
            </figure>
          ) : null}
        </section>
      )}

      <section className="vmd-blessing">
        <p>{tagline}</p>
      </section>

      <section className="vmd-schedule">
        <h2>Sacred Ceremonies</h2>
        <ul>
          {ceremonies.map((c) => (
            <li key={c.key} className="vmd-card">
              <h3>{c.label}</h3>
              {c.date ? <p>{c.date}</p> : null}
              {c.time ? <p>{c.time}</p> : null}
              {c.venue ? <p>{c.venue}</p> : null}
            </li>
          ))}
        </ul>
      </section>

      {customVideoUrl ? (
        <section className="vmd-video">
          <h2>Our Pre-Wedding Story & Special Memories</h2>
          <video controls playsInline src={customVideoUrl} />
        </section>
      ) : null}

      {mapsUrl ? (
        <section className="vmd-map">
          <a href={mapsUrl} target="_blank" rel="noreferrer">
            Get directions
          </a>
        </section>
      ) : null}

      <section id="vmd-rsvp" className="vmd-rsvp">
        <h2>Bless us with your presence</h2>
        <p>Kindly confirm your attendance. We look forward to celebrating with you.</p>
        {rsvpHref ? (
          <a className="vmd-cta" href={rsvpHref}>
            Open RSVP
          </a>
        ) : (
          <p className="vmd-rsvp-hint">RSVP will appear when this invitation is published.</p>
        )}
      </section>
    </div>
  );
}

registry.register({ metadata: META, component: VedicMandirDivine });
